import { create } from 'zustand';

/**
 * Pin UI state (#1854) — which objects render with a filled pin, and the optimistic overrides that
 * move a row to / from the top of its group instantly. A 1:1 mirror of `favorites-store.ts`.
 *
 * Each list hook runs a dedicated pinned query (`extendParams.pinnedOnly`) and reconciles the
 * returned ids into this store, scoped by a key like `screens:item` / `screens:group`; the browse
 * "rest" query sends `excludePinned` so a pinned row is never listed twice. A row's pin is filled
 * iff its id is in some scope's set (or has a pending optimistic override).
 *
 * Memory-only (NOT persisted), for the same reason as favorites: the server is the source of truth,
 * and a persisted override fought Apollo's persisted cache on reload.
 */
interface PinnedState {
  /** Authoritative pinned ids per scope key (e.g. 'screens:item', 'assets:group'). */
  scopes: Map<string, Set<string>>;
  /** Optimistic overrides: objectId -> desired pinned state, until the next reconcile. */
  pending: Map<string, boolean>;
  /** Replace a scope's pinned ids from a server pinned-query result; settles overrides. */
  reconcile: (scopeKey: string, pinnedIds: string[]) => void;
  /** Optimistically set an object's pinned state — drives the instant move on click. */
  setOptimistic: (objectId: string, pinned: boolean) => void;
  /** Drop an optimistic override (toggle failure → revert to server truth). */
  clearOptimistic: (objectId: string) => void;
  /** Forget the given ids entirely (after a delete / unpair) — a local prune, not an unpin. */
  purge: (objectIds: string[]) => void;
  /** Wipe all pin state. Called on logout + team switch (pins are per-(user, team)). */
  reset: () => void;
}

export const usePinnedStore = create<PinnedState>((set) => ({
  scopes: new Map(),
  pending: new Map(),
  reconcile: (scopeKey, pinnedIds) =>
    set((state) => {
      const scopes = new Map(state.scopes);
      const prev = scopes.get(scopeKey) ?? new Set<string>();
      const next = new Set(pinnedIds);
      scopes.set(scopeKey, next);
      // Settle overrides for any id that was in this scope before (unpinned) or is in it now
      // (pinned) — the server has now confirmed the truth for them.
      const pending = new Map(state.pending);
      for (const id of prev) pending.delete(id);
      for (const id of next) pending.delete(id);
      return { scopes, pending };
    }),
  setOptimistic: (objectId, pinned) =>
    set((state) => {
      const pending = new Map(state.pending);
      pending.set(objectId, pinned);
      return { pending };
    }),
  clearOptimistic: (objectId) =>
    set((state) => {
      if (!state.pending.has(objectId)) return state;
      const pending = new Map(state.pending);
      pending.delete(objectId);
      return { pending };
    }),
  purge: (objectIds) =>
    set((state) => {
      if (!objectIds.length) return state;
      const doomed = new Set(objectIds);
      let scopesChanged = false;
      const scopes = new Map(state.scopes);
      for (const [scopeKey, pinnedIds] of scopes) {
        if (!objectIds.some((id) => pinnedIds.has(id))) continue;
        const next = new Set(pinnedIds);
        for (const id of doomed) next.delete(id);
        scopes.set(scopeKey, next);
        scopesChanged = true;
      }
      const pendingChanged = objectIds.some((id) => state.pending.has(id));
      if (!scopesChanged && !pendingChanged) return state;
      const pending = new Map(state.pending);
      for (const id of doomed) pending.delete(id);
      return {
        ...(scopesChanged ? { scopes } : {}),
        ...(pendingChanged ? { pending } : {}),
      };
    }),
  reset: () => set({ scopes: new Map(), pending: new Map() }),
}));

/** Non-hook read of an object's current pinned state (override wins, then scope membership). */
export function isObjectPinned(state: PinnedState, objectId: string): boolean {
  const override = state.pending.get(objectId);
  if (override !== undefined) return override;
  for (const ids of state.scopes.values()) if (ids.has(objectId)) return true;
  return false;
}

/** Reactive selector — is this object currently shown as pinned? */
export function useIsPinned(objectId: string): boolean {
  return usePinnedStore((s) => isObjectPinned(s, objectId));
}

/** Subscribe a list hook to the optimistic-override map (re-renders on any pin toggle). */
export function usePendingPins(): Map<string, boolean> {
  return usePinnedStore((s) => s.pending);
}

/**
 * Pinned-first merge for ONE group, without refetching (#1854) — the mirror of the original
 * favorites `partitionFavorites` (`feature/ilhoon/favorites`). `pinnedItems` = the pinnedOnly query
 * result; `restItems` = the browse query result (pinned excluded server-side). Optimistic overrides
 * win, so a pin moves a rest row to the top and an unpin drops a pinned row back, instantly:
 *   - pinned = pinnedItems still pinned ++ rest rows optimistically pinned
 *   - rest   = pinnedItems optimistically unpinned (not in the excluded rest result yet) ++ the rest
 * Returns the group's rows in order: `[...pinned, ...rest]`.
 */
export function partitionPinned<T>(
  pinnedItems: T[],
  restItems: T[],
  getId: (t: T) => string,
  pending: Map<string, boolean>,
): T[] {
  const pinnedIdSet = new Set(pinnedItems.map(getId));
  const pinned = [
    ...pinnedItems.filter((i) => pending.get(getId(i)) !== false),
    ...restItems.filter((i) => pending.get(getId(i)) === true && !pinnedIdSet.has(getId(i))),
  ];
  const rest = [
    ...pinnedItems.filter((i) => pending.get(getId(i)) === false),
    ...restItems.filter((i) => pending.get(getId(i)) !== true && !pinnedIdSet.has(getId(i))),
  ];
  return [...pinned, ...rest];
}
